import {
  call,
  delay,
  put,
  race,
  select,
  take,
  takeLeading,
} from 'redux-saga/effects';
import {
  SET_PARAM_LAYOUT_DIMENSIONS,
  CHANGE_LAYOUT_DIMENSIONS,
  getParamLayoutDimensions,
  setParamLayoutDimensions,
} from '@corefront/redux-aba';
import {
  containerSelector,
  isNativeSelector,
  layoutDimensionsSelector,
} from '../../reducers/app/params';
import {
  setCSSVariablesToElement,
  getShellNameByContainer,
  getShellDefaultLayoutDimensions,
} from '../../../utils/shells';
import { externalNavbarEnabledChecker } from '../navbar';
import type { Action } from 'redux';
import { isEmpty, pathOr } from 'ramda';

interface PayloadAction<TPayload> extends Action {
  payload: TPayload;
}

export const UI_PARAMS_TIMEOUT = 50;

export function* getLayoutDimensions() {
  const isNative: ReturnType<typeof isNativeSelector> = yield select(
    isNativeSelector
  );

  // For native devices, we do not make requests for layout dimensions, because the Android version did not implement this data.
  // As fallback we use default layout dimensions for native device
  if (!isNative) {
    yield put(getParamLayoutDimensions());
  }

  const {
    timeout,
    settings,
  }: {
    timeout: boolean;
    settings: ReturnType<typeof setParamLayoutDimensions>;
  } = yield race({
    settings: take(SET_PARAM_LAYOUT_DIMENSIONS),
    timeout: delay(UI_PARAMS_TIMEOUT),
  });
  const dimensions = pathOr({}, ['payload'], settings);

  if (timeout || isEmpty(dimensions)) {
    const container: ReturnType<typeof containerSelector> = yield select(
      containerSelector
    );

    const shell = getShellNameByContainer(container);
    const dimensions = getShellDefaultLayoutDimensions(shell);

    yield put(setParamLayoutDimensions(dimensions));
  }
}

export function* applyDimensions(
  dimensions: ReturnType<typeof layoutDimensionsSelector>
) {
  const container: ReturnType<typeof containerSelector> = yield select(
    containerSelector
  );
  const isExternalNavbarEnabled = yield call(externalNavbarEnabledChecker);
  yield call(
    setCSSVariablesToElement,
    dimensions,
    container,
    isExternalNavbarEnabled
  );
}

export function* changeLayoutDimensions(
  action: PayloadAction<ReturnType<typeof layoutDimensionsSelector>>
) {
  const dimensions: ReturnType<typeof layoutDimensionsSelector> = action.payload
    ? action.payload
    : yield select(layoutDimensionsSelector);
  yield call(applyDimensions, dimensions);
}

export function* applyLayoutDimensions() {
  const dimensions: ReturnType<typeof layoutDimensionsSelector> = yield select(
    layoutDimensionsSelector
  );
  yield call(applyDimensions, dimensions);
}

export function* layoutDimensions() {
  yield takeLeading(CHANGE_LAYOUT_DIMENSIONS, changeLayoutDimensions);
  yield takeLeading(SET_PARAM_LAYOUT_DIMENSIONS, applyLayoutDimensions);
}
